
/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// MUI
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftPagination from "components/SoftPagination";

// Layout (unchanged)
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Table
import DataTable from "examples/Tables/DataTable";

// Cells
import DefaultCell from "layouts/userdata/components/DefaultCell";
import StatusCell from "layouts/userdata/components/StatusCell";
import CustomerCell from "layouts/userdata/components/CustomerCell";

// Hooks
import { useDropdownOptions } from "./hooks/useDropdownOptions";
import UserFilters from "./components/useFilters";
import { useUsersData } from "./hooks/useUsersData";

// Debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

function WorkCalenderTable({ embedded = false }) {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    order_by: "id",
    order: "desc",
  });

  const { dropdownOptions, loading: optionsLoading, fetchShiftsForBranch } =
    useDropdownOptions();

  const {
    users,
    loading: usersLoading,
    pagination,
    fetchUsers,
    setPagination,
  } = useUsersData();

  useEffect(() => {
    fetchUsers(
      filters,
      debouncedSearch,
      pagination.current_page,
      pagination.per_page
    );
  }, [
    filters,
    debouncedSearch,
    pagination.current_page,
    pagination.per_page,
  ]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const dataTableData = {
    columns: [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value, row }) => (
          <CustomerCell
            name={value}
            image={row.original.image || ""}
            color={row.original.color || "dark"}
          />
        ),
      },
      { Header: "Weekend off", accessor: "weekend_off", Cell: DefaultCell },
      { Header: "Branch", accessor: "branch.name", Cell: DefaultCell },
      { Header: "Shift", accessor: "shift.type", Cell: DefaultCell },
      {
        Header: "Shift Time",
        accessor: (row) => row.shift,
        id: "shift_time",
        Cell: ({ value }) => {
          console.log("Shift value:", value);

          if (!value?.start_time || !value?.end_time) {
            return <DefaultCell value="-" />;
          }

          return (
            <DefaultCell
              value={`${value.start_time} - ${value.end_time}`}
            />
          );
        }
      },

      { Header: "Department", accessor: "department.name", Cell: DefaultCell },
      { Header: "Designation", accessor: "designation.name", Cell: DefaultCell },
      { Header: "Role", accessor: "role", Cell: DefaultCell }



    ],
    rows: users,
  };

  const loading = optionsLoading || usersLoading;

  // 🔹 MAIN CONTENT (Reusable)
  const Content = (
    <SoftBox mb={embedded ? 0 : 3}>
      <Card>
        <SoftBox p={2} display="flex" flexDirection="column" gap={2}>
          <UserFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            dropdownOptions={dropdownOptions}
            fetchShiftsForBranch={fetchShiftsForBranch}
          />

          {loading ? (
            <SoftBox display="flex" justifyContent="center" py={5}>
              <CircularProgress color="info" />
            </SoftBox>
          ) : users.length === 0 ? (
            <SoftBox textAlign="center" py={4}>
              <Icon sx={{ fontSize: 40, color: "text.secondary" }}>
                search_off
              </Icon>
              <SoftTypography>No records found</SoftTypography>
            </SoftBox>
          ) : (
            <DataTable
              table={dataTableData}
              entriesPerPage={{
                defaultValue: pagination.per_page,
                entries: [10, 25, 50, 100],
              }}
              canSearch={false}
            />
          )}

          <SoftPagination variant="gradient" color="info">
            {Array.from({ length: pagination.last_page }, (_, i) => (
              <SoftPagination
                key={i}
                item
                active={pagination.current_page === i + 1}
                onClick={() =>
                  setPagination((p) => ({ ...p, current_page: i + 1 }))
                }
              >
                {i + 1}
              </SoftPagination>
            ))}
          </SoftPagination>
        </SoftBox>
      </Card>
    </SoftBox>
  );

  // 🔹 CONDITIONAL LAYOUT (NO BREAKING CHANGE)
  if (embedded) return Content;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {Content}
      <Footer />
    </DashboardLayout>
  );
}

WorkCalenderTable.propTypes = {
  embedded: PropTypes.bool,
};

export default WorkCalenderTable;
